/**
 * Garage Dashboard View
 * ROI / Time Saved (all users) + usage analytics (admin)
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TrendingUp, TrendingDown, Users, Zap, Activity, RefreshCw, Award, BarChart2, Calendar, Search, X, Trophy, Medal, Clock, Sparkles, Network } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import garageAPI from '../../../services/garage/api';
import { filterDashboardUsers } from '../../../utils/dashboardUserFilters';
import ToolSessionDetailsModal from '../modals/ToolSessionDetailsModal';
import UserDetailsModal from '../modals/UserDetailsModal';
import ToolTopUsersModal from '../modals/ToolTopUsersModal';
import UserToolsModal from '../modals/UserToolsModal';
import GarageRoiDashboardSection from '../components/GarageRoiDashboardSection';
import GarageMyTeamSection from '../components/GarageMyTeamSection';
import GarageLoadingScreen, { GarageLoadingRegion } from '../components/GarageLoadingScreen';
import TourGuide from '../components/TourGuide';
import { getDashboardRoiTourSteps } from '../tours/dashboardRoiTour';
import { DASHBOARD_USAGE_TOUR_STEPS } from '../tours/dashboardUsageTour';
import { getAdminPersonaScopes } from '../constants/roleAccess';

const GarageDashboardView = ({
  isAdmin = false,
  canAccessDashboard = false,
  onNavigate,
  userName = '',
  userRole = null,
  accessPersona = null,
  accessPersonas = [],
  showOnboarding = false,
  onboardingChecked = true,
}) => {
  // Metrics admin: admin ROI + Usage on Tools Dashboard only (no Admin nav)
  const showAdminDashboard = Boolean(canAccessDashboard || isAdmin);
  const adminPersonaScopes = userRole === 'admin'
    ? []
    : getAdminPersonaScopes(userRole, accessPersonas, accessPersona);
  const [loading, setLoading] = useState(showAdminDashboard);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [topTools, setTopTools] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [sessionTrends, setSessionTrends] = useState([]);
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [usersSearchLoading, setUsersSearchLoading] = useState(false);
  const [error, setError] = useState(null);

  // Page-level dashboard mode: ROI stack vs Usage stack vs My Team
  const [dashboardTab, setDashboardTab] = useState('roi');
  const [showMyTeamTab, setShowMyTeamTab] = useState(false);
  const [canBrowseOrg, setCanBrowseOrg] = useState(false);
  const [teamRefreshKey, setTeamRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ctx = await garageAPI.getMyTeamContext();
        if (cancelled) return;
        // Managers see their own line; dashboard admins can browse any org
        const canBrowse = Boolean(ctx?.canBrowseOrg);
        setCanBrowseOrg(canBrowse);
        setShowMyTeamTab(
          Boolean(ctx?.configured && (ctx?.hasDirectReports || canBrowse))
        );
      } catch (err) {
        console.warn('My Team context unavailable', err);
        if (!cancelled) {
          setShowMyTeamTab(false);
          setCanBrowseOrg(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Spotlight help tours
  const [showTour, setShowTour] = useState(false);
  const [tourKind, setTourKind] = useState('roi');

  useEffect(() => {
    if (!onboardingChecked || showOnboarding) {
      setShowTour(false);
      return;
    }
    // First visit → ROI tour (default tab)
    const roiDone = localStorage.getItem('tour-completed-dashboard-roi') === 'true';
    if (!roiDone) {
      setTourKind('roi');
      setShowTour(true);
    }
  }, [onboardingChecked, showOnboarding]);

  useEffect(() => {
    if (!onboardingChecked || showOnboarding || !showAdminDashboard) return;
    if (dashboardTab !== 'usage') return;
    const usageDone = localStorage.getItem('tour-completed-dashboard-usage') === 'true';
    if (!usageDone) {
      setTourKind('usage');
      setShowTour(true);
    }
  }, [dashboardTab, onboardingChecked, showOnboarding, showAdminDashboard]);

  const startHelpTour = () => {
    if (dashboardTab === 'usage' && showAdminDashboard) {
      setTourKind('usage');
    } else {
      setTourKind('roi');
    }
    setShowTour(true);
  };

  const tourSteps =
    tourKind === 'usage'
      ? DASHBOARD_USAGE_TOUR_STEPS
      : getDashboardRoiTourSteps(showAdminDashboard);

  const tourStorageKey =
    tourKind === 'usage'
      ? 'tour-completed-dashboard-usage'
      : 'tour-completed-dashboard-roi';

  const HelpButton = () => (
    <button
      type="button"
      className="garage-btn garage-btn-secondary"
      onClick={startHelpTour}
      title="Take a quick tour"
    >
      <Sparkles size={16} />
      Help
    </button>
  );

  const dashboardSubtitle =
    dashboardTab === 'roi'
      ? 'Declared time saved across the Apps and Gems catalog'
      : dashboardTab === 'team'
        ? 'Usage and time saved for people in your reporting line'
        : 'Usage, adoption, and session trends across the Apps and Gems catalog';

  const DashboardPageTabs = ({ includeUsage = true }) => (
    <div className="garage-dash-page-tabs" role="tablist" aria-label="Dashboard view" data-tour="dashboard-tabs">
      <button
        type="button"
        role="tab"
        aria-selected={dashboardTab === 'roi'}
        className={`garage-dash-page-tab ${dashboardTab === 'roi' ? 'active' : ''}`}
        onClick={() => setDashboardTab('roi')}
      >
        <Clock size={15} />
        Time Saved (ROI)
      </button>
      {includeUsage && (
        <button
          type="button"
          role="tab"
          aria-selected={dashboardTab === 'usage'}
          className={`garage-dash-page-tab ${dashboardTab === 'usage' ? 'active' : ''}`}
          onClick={() => setDashboardTab('usage')}
        >
          <Activity size={15} />
          Usage
        </button>
      )}
      {showMyTeamTab && (
        <button
          type="button"
          role="tab"
          aria-selected={dashboardTab === 'team'}
          className={`garage-dash-page-tab ${dashboardTab === 'team' ? 'active' : ''}`}
          onClick={() => setDashboardTab('team')}
        >
          <Network size={15} />
          My Team
        </button>
      )}
    </div>
  );

  // Pagination states
  const [toolsPage, setToolsPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [toolsPerPage] = useState(10);
  const [usersPerPage] = useState(10);

  // Search states
  const [toolsSearchQuery, setToolsSearchQuery] = useState('');
  const [usersSearchQuery, setUsersSearchQuery] = useState('');
  const [toolsPersonaFilter, setToolsPersonaFilter] = useState('all');

  // Limit states (for API - fetch more than needed)
  const [toolsLimit, setToolsLimit] = useState(50);
  const [usersLimit, setUsersLimit] = useState(50);

  // Filter states
  const [filters, setFilters] = useState({
    dateRange: '7d',
    toolType: 'all',
    customStartDate: null,
    customEndDate: null
  });
  const [loadedFilters, setLoadedFilters] = useState(filters);

  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Staging state for custom date picker (prevents immediate API calls)
  const [stagingStartDate, setStagingStartDate] = useState(null);
  const [stagingEndDate, setStagingEndDate] = useState(null);

  // Ref for custom date picker dropdown (for outside click detection)
  const datePickerRef = useRef(null);

  // Modal states
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Tool Top Users Modal states
  const [toolUsersModalOpen, setToolUsersModalOpen] = useState(false);
  const [selectedToolForUsers, setSelectedToolForUsers] = useState(null);
  const [toolTopUsers, setToolTopUsers] = useState([]);
  const [loadingToolUsers, setLoadingToolUsers] = useState(false);

  // User Tools Modal states
  const [userToolsModalOpen, setUserToolsModalOpen] = useState(false);
  const [userTools, setUserTools] = useState([]);
  const [loadingUserTools, setLoadingUserTools] = useState(false);

  // Tool selection for graph (multi-line trends)
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const [multiToolTrends, setMultiToolTrends] = useState({});
  const [loadingMultiToolTrends, setLoadingMultiToolTrends] = useState(false);
  const dashboardRequestRef = useRef(0);
  const multiToolTrendsRequestRef = useRef(0);
  const usersSearchRequestRef = useRef(0);

  const loadDashboardData = async () => {
    // Usage analytics are admin-only; ROI section loads itself.
    if (!showAdminDashboard) {
      setLoading(false);
      return;
    }
    const requestId = ++dashboardRequestRef.current;
    console.log('🔄 Loading dashboard data with filters:', filters);
    setLoading(true);
    setError(null);
    try {
      // Fetch all dashboard data in parallel with filters
      // Now weekly trends support custom date ranges too!
      const [stats, tools, users, trends] = await Promise.all([
        garageAPI.getDashboardStats(filters),
        garageAPI.getTopToolsByUsage(toolsLimit, filters),
        garageAPI.getTopUsersByUsage(usersLimit, filters),
        garageAPI.getDailySessionTrends(filters)
      ]);

      if (requestId !== dashboardRequestRef.current) return;
      console.log('✅ Dashboard data loaded successfully:', { stats, tools: tools.length, users: users.length, trends: trends.length });
      setDashboardStats(stats);
      setTopTools(tools);
      setTopUsers(users);
      setSessionTrends(trends);
      setLoadedFilters(filters);
    } catch (err) {
      if (requestId !== dashboardRequestRef.current) return;
      console.error('❌ Failed to load dashboard data:', err);
      console.error('Error details:', err.response || err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      if (requestId === dashboardRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const refreshCurrentDashboardTab = () => {
    if (dashboardTab === 'team') {
      setTeamRefreshKey((key) => key + 1);
      return;
    }
    loadDashboardData();
  };

  useEffect(() => {
    loadDashboardData();
  }, [toolsLimit, usersLimit, filters, showAdminDashboard]);

  // Server-side user search (beyond top 50)
  useEffect(() => {
    const query = usersSearchQuery.trim();
    const requestId = ++usersSearchRequestRef.current;
    if (!query) {
      setSearchedUsers([]);
      setUsersSearchLoading(false);
      return undefined;
    }

    setUsersSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await garageAPI.searchUsersByUsage(query, filters, 50);
        if (requestId !== usersSearchRequestRef.current) return;
        setSearchedUsers(results);
      } catch (err) {
        if (requestId !== usersSearchRequestRef.current) return;
        console.error('Failed to search users:', err);
        setSearchedUsers([]);
      } finally {
        if (requestId === usersSearchRequestRef.current) {
          setUsersSearchLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [usersSearchQuery, filters]);

  // Load multi-tool trends when selection or filters change
  useEffect(() => {
    if (selectedToolIds.length > 0) {
      loadMultiToolTrends();
    } else {
      multiToolTrendsRequestRef.current += 1;
      setMultiToolTrends({});
      setLoadingMultiToolTrends(false);
    }
  }, [selectedToolIds, filters]);

  // Handle outside click for custom date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on a date input or its calendar picker
      const isDateInput = event.target.closest('input[type="date"]');
      const isCalendarPicker = event.target.classList.contains('garage-date-input-icon');

      if (isDateInput || isCalendarPicker) {
        return; // Allow interaction with date inputs
      }

      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        // Close the picker without applying changes
        setShowCustomDatePicker(false);
      }
    };

    if (showCustomDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCustomDatePicker]);

  const loadMultiToolTrends = async () => {
    const requestId = ++multiToolTrendsRequestRef.current;
    setLoadingMultiToolTrends(true);
    try {
      const trends = await garageAPI.getMultiToolWeeklyTrends(selectedToolIds, filters);
      if (requestId !== multiToolTrendsRequestRef.current) return;
      setMultiToolTrends(trends);
    } catch (err) {
      if (requestId !== multiToolTrendsRequestRef.current) return;
      console.error('Failed to load multi-tool trends:', err);
      setMultiToolTrends({});
    } finally {
      if (requestId === multiToolTrendsRequestRef.current) {
        setLoadingMultiToolTrends(false);
      }
    }
  };

  const handleToolRowClick = (tool) => {
    setSelectedToolIds(prev => {
      if (prev.includes(tool.toolId)) {
        // Deselect
        return prev.filter(id => id !== tool.toolId);
      } else {
        // Select (limit to 5 tools for chart readability)
        if (prev.length >= 5) {
          return prev; // Don't add more than 5
        }
        return [...prev, tool.toolId];
      }
    });
  };

  const handleViewToolSessions = (tool) => {
    setSelectedTool(tool);
    setSessionModalOpen(true);
  };

  const handleViewUserDetails = async (user) => {
    setSelectedUser(user);
    setUserToolsModalOpen(true);
    setLoadingUserTools(true);
    setUserTools([]); // Clear previous data
    try {
      // FIXED: Pass toolType filter to getUserTools API
      const tools = await garageAPI.getUserTools(user.userEmail, 50, filters);
      setUserTools(tools);
    } catch (err) {
      console.error('Failed to load user tools:', err);
      setUserTools([]);
    } finally {
      setLoadingUserTools(false);
    }
  };

  const handleViewToolTopUsers = async (tool) => {
    setSelectedToolForUsers(tool);
    setToolUsersModalOpen(true);
    setLoadingToolUsers(true);
    setToolTopUsers([]); // Clear previous data
    try {
      const users = await garageAPI.getToolTopUsers(tool.toolId, 10, filters);
      setToolTopUsers(filterDashboardUsers(users));
    } catch (err) {
      console.error('Failed to load tool top users:', err);
      setToolTopUsers([]);
    } finally {
      setLoadingToolUsers(false);
    }
  };

  // Format number with K/M suffixes
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Calculate percentage change
  const calculateChange = (current, previous) => {
    if (!previous) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Get user initials from email
  const getUserInitials = (email) => {
    if (!email) return '?';
    const name = email.split('@')[0];
    const parts = name.split('.');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Format date for chart X-axis
  const formatChartDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };

  // Get random color for user avatar
  const getUserColor = (index) => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52C4B5'];
    return colors[index % colors.length];
  };

  const RankDisplay = ({ rank }) => {
    const index = rank;
    if (index === 0) {
      return (
        <span className="garage-rank-badge garage-rank-badge-gold" title="Rank 1">
          <Trophy size={14} strokeWidth={2.25} />
        </span>
      );
    }
    if (index === 1) {
      return (
        <span className="garage-rank-badge garage-rank-badge-silver" title="Rank 2">
          <Medal size={14} strokeWidth={2.25} />
        </span>
      );
    }
    if (index === 2) {
      return (
        <span className="garage-rank-badge garage-rank-badge-bronze" title="Rank 3">
          <Medal size={14} strokeWidth={2.25} />
        </span>
      );
    }
    return <span className="garage-rank-number">#{index + 1}</span>;
  };

  const filterIconColor = (isActive) => (isActive ? '#fff' : 'var(--garage-text)');

  const DateInputField = ({ value, onChange }) => (
    <div className="garage-date-input-wrap">
      <input
        type="date"
        className="garage-date-input"
        value={value}
        onChange={onChange}
      />
      <Calendar size={16} className="garage-date-input-icon" aria-hidden="true" />
    </div>
  );

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle custom date range selection
  const handleCustomDateRange = (startDate, endDate) => {
    setFilters({
      ...filters,
      dateRange: 'custom',
      customStartDate: startDate,
      customEndDate: endDate
    });
    setShowCustomDatePicker(false);
  };

  // Clear custom date range
  const clearCustomDateRange = () => {
    setStagingStartDate(null);
    setStagingEndDate(null);
    setFilters({
      ...filters,
      dateRange: '7d',
      customStartDate: null,
      customEndDate: null
    });
    setShowCustomDatePicker(false);
  };

  // Apply custom date range (only updates filters when user clicks Apply)
  const applyCustomDateRange = () => {
    if (stagingStartDate && stagingEndDate) {
      setFilters({
        ...filters,
        dateRange: 'custom',
        customStartDate: stagingStartDate,
        customEndDate: stagingEndDate
      });
      setShowCustomDatePicker(false);
    }
  };

  // Pagination helpers
  const getPaginatedData = (data, page, perPage) => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (data, perPage) => {
    return Math.ceil(data.length / perPage);
  };

  const handleToolsPageChange = (newPage) => {
    setToolsPage(newPage);
  };

  const handleUsersPageChange = (newPage) => {
    setUsersPage(newPage);
  };

  const getEnvTagColor = (env) => {
    if (env === 'PRD') return 'green';
    if (env === 'TEST') return 'blue';
    if (env === 'DEV') return 'purple';
    if (env === 'STAGING') return 'yellow';
    return 'gray';
  };

  // Distinct personas present among the currently loaded tools (alphabetical)
  const toolsPersonaOptions = useMemo(() => {
    const values = new Set();
    (topTools || []).forEach((tool) => {
      if (tool.persona && tool.persona.trim()) values.add(tool.persona.trim());
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [topTools]);

  // Filter tools based on persona selection + search query
  const filteredTools = useMemo(() => {
    let rows = topTools;
    if (toolsPersonaFilter !== 'all') {
      rows = rows.filter(tool => (tool.persona || '').trim() === toolsPersonaFilter);
    }
    if (toolsSearchQuery.trim()) {
      const query = toolsSearchQuery.toLowerCase();
      rows = rows.filter(tool =>
        tool.toolTitle.toLowerCase().includes(query) ||
        tool.toolType.toLowerCase().includes(query) ||
        (tool.persona && tool.persona.toLowerCase().includes(query)) ||
        (tool.environment && tool.environment.toLowerCase().includes(query))
      );
    }
    return rows;
  }, [topTools, toolsSearchQuery, toolsPersonaFilter]);

  const displayUsers = useMemo(() => {
    const source = usersSearchQuery.trim() ? searchedUsers : topUsers;
    return filterDashboardUsers(source);
  }, [usersSearchQuery, searchedUsers, topUsers]);

  // Get paginated tools and users (from filtered results)
  const paginatedTools = getPaginatedData(filteredTools, toolsPage, toolsPerPage);
  const paginatedUsers = getPaginatedData(displayUsers, usersPage, usersPerPage);
  const totalToolsPages = getTotalPages(filteredTools, toolsPerPage);
  const totalUsersPages = getTotalPages(displayUsers, usersPerPage);

  const getSessionsColumnLabel = () => {
    if (loadedFilters.dateRange === 'custom' && loadedFilters.customStartDate && loadedFilters.customEndDate) {
      return 'Sessions (custom)';
    }
    switch (loadedFilters.dateRange) {
      case '7d': return 'Sessions (7d)';
      case '30d': return 'Sessions (30d)';
      case '90d': return 'Sessions (90d)';
      case 'all': return 'Sessions (all)';
      default: return 'Sessions';
    }
  };

  const getToolSessionsCount = (tool) => tool.sessionsInRange ?? tool.sessions7d ?? 0;

  // Get display text for current date filter
  const getDateFilterDisplay = (rangeFilters = loadedFilters) => {
    if (rangeFilters.dateRange === 'custom' && rangeFilters.customStartDate && rangeFilters.customEndDate) {
      const start = new Date(rangeFilters.customStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = new Date(rangeFilters.customEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${start} - ${end}`;
    }
    switch (rangeFilters.dateRange) {
      case '7d': return 'Last 7 days';
      case '30d': return 'Last 30 days';
      case '90d': return 'Last 90 days';
      case 'all': return 'All time';
      default: return 'Last 7 days';
    }
  };

  if (loading && showAdminDashboard && !dashboardStats) {
    return (
      <div className="garage-content">
        <div className="garage-page-hero">
          <div>
            <h1 className="garage-page-title">Tools Dashboard</h1>
            <p className="garage-page-subtitle">{dashboardSubtitle}</p>
          </div>
          <div className="garage-hero-actions">
            <HelpButton />
          </div>
        </div>
        <DashboardPageTabs />
        {dashboardTab === 'roi' ? (
          <GarageRoiDashboardSection
            isAdmin={showAdminDashboard}
            onOpenRoiConfig={
              isAdmin
                ? () => onNavigate?.('admin', { adminTab: 'roi' })
                : undefined
            }
            userName={userName}
            userRole={userRole}
            accessPersona={accessPersona}
            accessPersonas={accessPersonas}
          />
        ) : dashboardTab === 'team' ? (
          <GarageMyTeamSection
            userName={userName}
            canBrowseOrg={canBrowseOrg}
            refreshKey={teamRefreshKey}
          />
        ) : (
          <GarageLoadingScreen message="Loading usage analytics…" />
        )}
        {showTour && !showOnboarding && (
          <TourGuide
            steps={tourSteps}
            storageKey={tourStorageKey}
            onComplete={() => setShowTour(false)}
            onSkip={() => setShowTour(false)}
          />
        )}
      </div>
    );
  }

  if (error && showAdminDashboard && !dashboardStats) {
    return (
      <div className="garage-content">
        <div className="garage-page-hero">
          <div>
            <h1 className="garage-page-title">Tools Dashboard</h1>
            <p className="garage-page-subtitle">{dashboardSubtitle}</p>
          </div>
          <div className="garage-hero-actions">
            <HelpButton />
            <button
              className="garage-btn garage-btn-secondary"
              onClick={loadDashboardData}
            >
              <RefreshCw size={16} />
              Retry
            </button>
          </div>
        </div>
        <DashboardPageTabs />
        {dashboardTab === 'roi' ? (
          <GarageRoiDashboardSection
            isAdmin={showAdminDashboard}
            onOpenRoiConfig={
              isAdmin
                ? () => onNavigate?.('admin', { adminTab: 'roi' })
                : undefined
            }
            userName={userName}
            userRole={userRole}
            accessPersona={accessPersona}
            accessPersonas={accessPersonas}
          />
        ) : dashboardTab === 'team' ? (
          <GarageMyTeamSection
            userName={userName}
            canBrowseOrg={canBrowseOrg}
            refreshKey={teamRefreshKey}
          />
        ) : (
          <div className="garage-placeholder-content">
            <p className="garage-placeholder-text" style={{ color: 'var(--garage-red)' }}>
              ⚠️ {error}
            </p>
          </div>
        )}
        {showTour && !showOnboarding && (
          <TourGuide
            steps={tourSteps}
            storageKey={tourStorageKey}
            onComplete={() => setShowTour(false)}
            onSkip={() => setShowTour(false)}
          />
        )}
      </div>
    );
  }

  // Non-admin: ROI (+ My Team when the user has direct reports)
  if (!showAdminDashboard) {
    return (
      <div className="garage-content">
        <div className="garage-page-hero">
          <div>
            <h1 className="garage-page-title">Tools Dashboard</h1>
            <p className="garage-page-subtitle">
              {dashboardTab === 'team'
                ? 'Usage and time saved for people in your reporting line'
                : 'Your Time Saved across the Apps and Gems catalog'}
            </p>
          </div>
          <div className="garage-hero-actions">
            <HelpButton />
          </div>
        </div>
        {showMyTeamTab && <DashboardPageTabs includeUsage={false} />}
        {dashboardTab === 'team' && showMyTeamTab ? (
          <GarageMyTeamSection
            userName={userName}
            canBrowseOrg={canBrowseOrg}
            refreshKey={teamRefreshKey}
          />
        ) : (
          <GarageRoiDashboardSection isAdmin={false} userName={userName} />
        )}
        {showTour && !showOnboarding && (
          <TourGuide
            steps={getDashboardRoiTourSteps(false)}
            storageKey="tour-completed-dashboard-roi"
            onComplete={() => setShowTour(false)}
            onSkip={() => setShowTour(false)}
          />
        )}
      </div>
    );
  }

  const stats = dashboardStats || {};
  // TODO: Backend needs to provide previous week stats for trend calculation
  // For now, show trends from weekly data if available
  const sessionsChange = sessionTrends.length >= 2
    ? calculateChange(sessionTrends[sessionTrends.length - 1].sessions, sessionTrends[sessionTrends.length - 2].sessions)
    : 0;
  const usersChange = sessionTrends.length >= 2
    ? calculateChange(sessionTrends[sessionTrends.length - 1].uniqueUsers, sessionTrends[sessionTrends.length - 2].uniqueUsers)
    : 0;

  // Prepare chart data - either aggregate or per-tool based on selection
  const prepareChartData = () => {
    if (selectedToolIds.length === 0) {
      return sessionTrends;
    }

    return sessionTrends.map((dayData) => {
      const combinedDay = {
        date: dayData.date,
        totalSessions: dayData.sessions,
        totalUsers: dayData.uniqueUsers
      };

      selectedToolIds.forEach(toolId => {
        const toolTrends = multiToolTrends[toolId];
        if (toolTrends) {
          const toolDay = toolTrends.find((d) => d.date === dayData.date);
          if (toolDay) {
            combinedDay[`${toolId}_sessions`] = toolDay.sessions;
            combinedDay[`${toolId}_users`] = toolDay.uniqueUsers;
          }
        }
      });

      return combinedDay;
    });
  };

  const chartData = prepareChartData();

  // Define colors for up to 5 tools
  const toolColors = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Orange
    '#8B5CF6', // Purple
    '#EF4444'  // Red
  ];

  // Pagination component
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '16px 20px',
        borderTop: '1px solid var(--garage-border)',
        backgroundColor: 'var(--garage-surface-2)'
      }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid var(--garage-border)',
            backgroundColor: currentPage === 1 ? 'var(--garage-surface-2)' : 'var(--garage-surface)',
            color: currentPage === 1 ? 'var(--garage-text-3)' : 'var(--garage-text)',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
        >
          Previous
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid var(--garage-border)',
                backgroundColor: 'var(--garage-surface)',
                color: 'var(--garage-text)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              1
            </button>
            {startPage > 2 && <span style={{ color: 'var(--garage-text-3)' }}>...</span>}
          </>
        )}

        {pageNumbers.map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid var(--garage-border)',
              backgroundColor: page === currentPage ? 'var(--garage-blue)' : 'var(--garage-surface)',
              color: page === currentPage ? '#fff' : 'var(--garage-text)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: page === currentPage ? '600' : '500',
              transition: 'all 0.2s'
            }}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span style={{ color: 'var(--garage-text-3)' }}>...</span>}
            <button
              onClick={() => onPageChange(totalPages)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid var(--garage-border)',
                backgroundColor: 'var(--garage-surface)',
                color: 'var(--garage-text)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid var(--garage-border)',
            backgroundColor: currentPage === totalPages ? 'var(--garage-surface-2)' : 'var(--garage-surface)',
            color: currentPage === totalPages ? 'var(--garage-text-3)' : 'var(--garage-text)',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
        >
          Next
        </button>

        <span style={{
          marginLeft: '12px',
          fontSize: '12px',
          color: 'var(--garage-text-3)'
        }}>
          Page {currentPage} of {totalPages}
        </span>
      </div>
    );
  };

  return (
    <div className="garage-content">
      <div className="garage-page-hero">
        <div>
          <h1 className="garage-page-title">Tools Dashboard</h1>
          <p className="garage-page-subtitle">{dashboardSubtitle}</p>
        </div>
        <div className="garage-hero-actions">
          {stats.lastDataSyncAt && (
            <span
              className="garage-dashboard-last-sync"
              title="Most recent successful session data sync across tools"
            >
              Last sync:{' '}
              {new Date(stats.lastDataSyncAt).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          )}
          <HelpButton />
          <button
            className="garage-btn garage-btn-secondary"
            onClick={refreshCurrentDashboardTab}
            disabled={dashboardTab === 'usage' && loading}
            aria-busy={dashboardTab === 'usage' && loading}
          >
            <RefreshCw size={16} />
            {loading && dashboardTab === 'usage' ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <DashboardPageTabs />

      {dashboardTab === 'roi' ? (
        <GarageRoiDashboardSection
          isAdmin={showAdminDashboard}
          onOpenRoiConfig={
              isAdmin
                ? () => onNavigate?.('admin', { adminTab: 'roi' })
                : undefined
            }
          userName={userName}
          userRole={userRole}
          accessPersona={accessPersona}
          accessPersonas={accessPersonas}
        />
      ) : dashboardTab === 'team' ? (
        <GarageMyTeamSection
          userName={userName}
          canBrowseOrg={canBrowseOrg}
          refreshKey={teamRefreshKey}
        />
      ) : (
        <>

      {adminPersonaScopes.length > 0 && (
        <div style={{
          padding: '10px 16px',
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#1565c0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Sparkles size={16} />
          <span>
            <strong>{adminPersonaScopes.join(', ')} Admin View:</strong> Showing usage analytics for assigned-persona apps &amp; gems only.
          </span>
        </div>
      )}

      {/* KPI Cards */}
      <GarageLoadingRegion loading={loading} showIndicator={false}>
      <div
        className="garage-dashboard-kpis"
        data-tour="usage-kpis"
        aria-busy={loading}
      >
        <div className="garage-kpi-card">
          <div className="garage-kpi-header">
            <span className="garage-kpi-label">Total Catalog</span>
            <Zap size={20} style={{ color: 'var(--garage-orange)' }} />
          </div>
          <span className="garage-kpi-value">{stats.totalTools || 0}</span>
          <span className="garage-kpi-sub">
            {stats.totalGems || 0} Gems · {stats.totalApps || 0} Apps
          </span>
        </div>

        <div className="garage-kpi-card">
          <div className="garage-kpi-header">
            <span className="garage-kpi-label">Sessions ({getDateFilterDisplay()})</span>
            <Activity size={20} style={{ color: 'var(--garage-blue)' }} />
          </div>
          <span className="garage-kpi-value">{formatNumber(stats.totalSessionsInRange ?? stats.totalSessions7d)}</span>
          <span className={`garage-kpi-sub ${parseFloat(sessionsChange) >= 0 ? 'green' : 'red'}`}>
            {parseFloat(sessionsChange) >= 0 ? (
              <TrendingUp size={12} />
            ) : (
              <TrendingDown size={12} />
            )}
            {Math.abs(sessionsChange)}% 
          </span>
        </div>

        <div className="garage-kpi-card">
          <div className="garage-kpi-header">
            <span className="garage-kpi-label">Active Users ({getDateFilterDisplay()})</span>
            <Users size={20} style={{ color: 'var(--garage-green)' }} />
          </div>
          <span className="garage-kpi-value">{stats.activeUsersInRange ?? stats.activeUsers7d ?? 0}</span>
          <span className={`garage-kpi-sub ${parseFloat(usersChange) >= 0 ? 'green' : 'red'}`}>
            {parseFloat(usersChange) >= 0 ? (
              <TrendingUp size={12} />
            ) : (
              <TrendingDown size={12} />
            )}
            {Math.abs(usersChange)}%
          </span>
        </div>

        <div className="garage-kpi-card">
          <div className="garage-kpi-header">
            <span className="garage-kpi-label">Avg Rating</span>
            <Award size={20} style={{ color: 'var(--garage-yellow)' }} />
          </div>
          <span className="garage-kpi-value">{stats.avgRating ? stats.avgRating.toFixed(1) : 'N/A'}</span>
          <span className="garage-kpi-sub">
            Based on all tool ratings
          </span>
        </div>
      </div>

      </GarageLoadingRegion>
      {/* Filters Bar - Full Width at Top */}
      <div className="garage-dashboard-filters-bar" data-tour="usage-filters">
        <div style={{ display: 'flex', alignItems: 'center', gap: '32px', flexWrap: 'wrap' }}>
          {/* Date Range Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
            <label style={{
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--garage-text-2)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <Calendar size={14} style={{ color: 'var(--garage-text-2)' }} />
              Date Range:
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {['7d', '30d', '90d', 'all'].map((range) => (
                <button
                  key={range}
                  onClick={() => {
                    setFilters({ ...filters, dateRange: range, customStartDate: null, customEndDate: null });
                    setShowCustomDatePicker(false);
                  }}
                  style={{
                    padding: '6px 16px',
                    borderRadius: '6px',
                    border: filters.dateRange === range ? '2px solid var(--garage-blue)' : '1px solid var(--garage-border)',
                    backgroundColor: filters.dateRange === range ? 'var(--garage-blue)' : 'var(--garage-bg)',
                    color: filters.dateRange === range ? '#fff' : 'var(--garage-text)',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: filters.dateRange === range ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (filters.dateRange !== range) {
                      e.currentTarget.style.borderColor = 'var(--garage-blue)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (filters.dateRange !== range) {
                      e.currentTarget.style.borderColor = 'var(--garage-border)';
                    }
                  }}
                >
                  {range === '7d' ? 'Last 7 days' : range === '30d' ? 'Last 30 days' : range === '90d' ? 'Last 90 days' : 'All time'}
                </button>
              ))}

              {/* Custom Date Range Button */}
              <button
                onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
                className="garage-date-filter-btn"
                style={{
                  padding: '6px 16px',
                  borderRadius: '6px',
                  border: filters.dateRange === 'custom' ? '2px solid var(--garage-blue)' : '1px solid var(--garage-border)',
                  backgroundColor: filters.dateRange === 'custom' ? 'var(--garage-blue)' : 'var(--garage-bg)',
                  color: filters.dateRange === 'custom' ? '#fff' : 'var(--garage-text)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: filters.dateRange === 'custom' ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  if (filters.dateRange !== 'custom') {
                    e.currentTarget.style.borderColor = 'var(--garage-blue)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (filters.dateRange !== 'custom') {
                    e.currentTarget.style.borderColor = 'var(--garage-border)';
                  }
                }}
              >
                <Calendar size={14} style={{ color: filterIconColor(filters.dateRange === 'custom'), flexShrink: 0 }} />
                Custom
              </button>
            </div>

            {/* Custom Date Picker Dropdown */}
            {showCustomDatePicker && (
              <div
                ref={datePickerRef}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '140px',
                  marginTop: '8px',
                  backgroundColor: 'var(--garage-surface)',
                  border: '1px solid var(--garage-border)',
                  borderRadius: '8px',
                  padding: '16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000,
                  minWidth: '320px'
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'var(--garage-text-2)',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    Start Date:
                  </label>
                  <DateInputField
                    value={formatDateForInput(stagingStartDate || filters.customStartDate)}
                    onChange={(e) => setStagingStartDate(e.target.value)}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'var(--garage-text-2)',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    End Date:
                  </label>
                  <DateInputField
                    value={formatDateForInput(stagingEndDate || filters.customEndDate)}
                    onChange={(e) => setStagingEndDate(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={clearCustomDateRange}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid var(--garage-border)',
                      backgroundColor: 'var(--garage-bg)',
                      color: 'var(--garage-text)',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Clear
                  </button>
                  <button
                    onClick={applyCustomDateRange}
                    disabled={!stagingStartDate || !stagingEndDate}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: (!stagingStartDate || !stagingEndDate) ? 'var(--garage-border)' : 'var(--garage-blue)',
                      color: '#fff',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: (!stagingStartDate || !stagingEndDate) ? 'not-allowed' : 'pointer',
                      opacity: (!stagingStartDate || !stagingEndDate) ? 0.5 : 1
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Tool Type Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--garage-text-2)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              🔧 Tool Type:
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { value: 'all', label: 'All' },
                { value: 'Gem', label: 'Gems' },
                { value: 'App', label: 'Apps' }
              ].map((type) => (
                <button
                  key={type.value}
                  onClick={() => {
                    setFilters({ ...filters, toolType: type.value });
                    // FIXED: Reset selected tools when changing tool type filter
                    setSelectedToolIds([]);
                  }}
                  style={{
                    padding: '6px 16px',
                    borderRadius: '6px',
                    border: filters.toolType === type.value ? '2px solid var(--garage-orange)' : '1px solid var(--garage-border)',
                    backgroundColor: filters.toolType === type.value ? 'var(--garage-orange)' : 'var(--garage-bg)',
                    color: filters.toolType === type.value ? '#fff' : 'var(--garage-text)',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: filters.toolType === type.value ? '0 2px 8px rgba(251, 146, 60, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (filters.toolType !== type.value) {
                      e.currentTarget.style.borderColor = 'var(--garage-orange)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (filters.toolType !== type.value) {
                      e.currentTarget.style.borderColor = 'var(--garage-border)';
                    }
                  }}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Active Filter Indicator */}
          <div style={{
            marginLeft: 'auto',
            padding: '6px 12px',
            backgroundColor: 'var(--garage-bg-2)',
            borderRadius: '6px',
            fontSize: '12px',
            color: 'var(--garage-text-3)'
          }}>
            Showing: <strong style={{ color: 'var(--garage-text)' }}>
              {getDateFilterDisplay()}
            </strong> · <strong style={{ color: 'var(--garage-text)' }}>
              {loadedFilters.toolType === 'all' ? 'All Tools' : loadedFilters.toolType === 'Gem' ? 'Gems Only' : 'Apps Only'}
            </strong>
          </div>
          {(loading || loadingMultiToolTrends) && (
            <GarageLoadingScreen compact message="Updating…" />
          )}
          {!loading && error && dashboardStats && (
            <span className="garage-dashboard-refresh-error" role="alert">
              Could not update. Showing previous data.
            </span>
          )}
        </div>
      </div>

      {/* FULL-WIDTH GRAPH AT TOP: Multi-line Trends for Top Tools */}
      <div
        className="garage-dashboard-chart-card"
        style={{ marginBottom: '32px' }}
        data-tour="usage-trends"
        aria-busy={loading || loadingMultiToolTrends}
      >
        <div className="garage-dashboard-chart-header" style={{ padding: '16px 20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <BarChart2 size={20} style={{ color: 'var(--garage-green)' }} />
              <span>{selectedToolIds.length > 0 ? 'Tool Comparison' : 'Usage Trends - All Tools'}</span>
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--garage-text-3)', marginLeft: '28px' }}>
              {selectedToolIds.length > 0
                ? `Comparing ${selectedToolIds.length} selected tool${selectedToolIds.length > 1 ? 's' : ''} · ${getDateFilterDisplay()}`
                : `Daily sessions and unique users · ${getDateFilterDisplay()}`
              }
            </p>
          </div>
          {selectedToolIds.length > 0 && (
            <button
              onClick={() => setSelectedToolIds([])}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: 'var(--garage-bg-2)',
                border: '1px solid var(--garage-border)',
                borderRadius: '6px',
                color: 'var(--garage-text-2)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--garage-border)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--garage-bg-2)';
              }}
            >
              Clear Selection
            </button>
          )}
        </div>
        <GarageLoadingRegion
          loading={loading || (loadingMultiToolTrends && selectedToolIds.length > 0)}
          showIndicator={false}
        >
        <div className="garage-dashboard-chart-body">
          {sessionTrends && sessionTrends.length > 0 && topTools && topTools.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData} margin={{ top: 10, right: 40, left: 20, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--garage-border-2)" opacity={0.5} />
                <XAxis
                  dataKey="date"
                  stroke="var(--garage-text-2)"
                  style={{ fontSize: '13px' }}
                  tick={{ fill: 'var(--garage-text-2)' }}
                  tickFormatter={formatChartDate}
                  minTickGap={20}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  stroke="var(--garage-text-2)"
                  style={{ fontSize: '13px' }}
                  tick={{ fill: 'var(--garage-text-2)' }}
                  label={{ value: 'Sessions', angle: -90, position: 'insideLeft', style: { fill: 'var(--garage-text-2)', fontSize: '13px' } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--garage-bg)',
                    border: '1px solid var(--garage-border)',
                    borderRadius: '10px',
                    padding: '14px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                  labelStyle={{ fontWeight: '600', marginBottom: '6px', color: 'var(--garage-text)' }}
                  itemStyle={{ fontSize: '13px', padding: '3px 0' }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '15px' }}
                  iconType="line"
                  iconSize={18}
                />

                {/* Render lines based on selection */}
                {selectedToolIds.length === 0 ? (
                  <>
                    {/* No tools selected - show aggregate data */}
                    <Line
                      type="monotone"
                      dataKey="sessions"
                      stroke="var(--garage-blue)"
                      strokeWidth={3}
                      name="Total Sessions"
                      dot={{ fill: 'var(--garage-blue)', r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="uniqueUsers"
                      stroke="var(--garage-green)"
                      strokeWidth={3}
                      name="Unique Users"
                      dot={{ fill: 'var(--garage-green)', r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </>
                ) : (
                  <>
                    {/* Tools selected - show per-tool lines */}
                    {selectedToolIds.map((toolId, index) => {
                      const tool = topTools.find(t => t.toolId === toolId);
                      if (!tool) return null;

                      return (
                        <Line
                          key={toolId}
                          type="monotone"
                          dataKey={`${toolId}_sessions`}
                          stroke={toolColors[index % toolColors.length]}
                          strokeWidth={3}
                          name={tool.toolTitle}
                          dot={{ fill: toolColors[index % toolColors.length], r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                      );
                    })}
                    {/* Show aggregate as dashed background reference */}
                    <Line
                      type="monotone"
                      dataKey="totalSessions"
                      stroke="var(--garage-text-3)"
                      strokeWidth={1.5}
                      strokeDasharray="5 5"
                      name="Total (All Tools)"
                      dot={false}
                      opacity={0.3}
                    />
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="garage-placeholder-content">
              <p className="garage-placeholder-text">No trend data available</p>
            </div>
          )}
        </div>
        </GarageLoadingRegion>
      </div>

      {/* Two-Column Layout: Tools Dashboard | Users Dashboard */}
      <GarageLoadingRegion loading={loading} showIndicator={false}>
      <div
        className="garage-dashboard-side-by-side"
        data-tour="usage-leaderboards"
        aria-busy={loading}
      >

        {/* LEFT COLUMN: TOOLS DASHBOARD */}
        <div className="garage-dashboard-panel">

          {/* Tools Leaderboard */}
          <div className="garage-dashboard-table-card">
            <div className="garage-dashboard-chart-header" style={{ padding: '16px 20px' }}>
              <div>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <Award size={18} style={{ color: 'var(--garage-yellow)' }} />
                  <span>Tools Leaderboard</span>
                </h3>
                <p style={{ fontSize: '12px', color: 'var(--garage-text-3)', marginLeft: '26px' }}>
                  {selectedToolIds.length > 0 ? (
                    <span>
                      <strong style={{ color: 'var(--garage-blue)' }}>{selectedToolIds.length} tool{selectedToolIds.length > 1 ? 's' : ''} selected</strong> for graph comparison ·
                      Click rows to select/deselect (max 5)
                    </span>
                  ) : (toolsSearchQuery.trim() || toolsPersonaFilter !== 'all') ? (
                    <span>
                      Showing <strong style={{ color: 'var(--garage-blue)' }}>{filteredTools.length}</strong> of {topTools.length} tools ({toolsPerPage} per page)
                    </span>
                  ) : (
                    <span>
                      Showing {topTools.length} tools ({toolsPerPage} per page) ·
                      Click rows to compare in graph above
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Search + Persona Filter Bar */}
            <div style={{
              padding: '12px 20px',
              backgroundColor: 'var(--garage-surface)',
              borderBottom: '1px solid var(--garage-border)',
              display: 'flex',
              gap: '10px',
              alignItems: 'center'
            }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={16} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--garage-text-3)'
                }} />
                <input
                  type="text"
                  placeholder="Search tools by name, type, or persona..."
                  value={toolsSearchQuery}
                  onChange={(e) => {
                    setToolsSearchQuery(e.target.value);
                    setToolsPage(1); // Reset to first page on search
                  }}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '8px 12px 8px 36px',
                    border: '1px solid var(--garage-border)',
                    borderRadius: '6px',
                    fontSize: '13px',
                    backgroundColor: 'var(--garage-bg)',
                    color: 'var(--garage-text)',
                    outline: 'none'
                  }}
                />
                {toolsSearchQuery && (
                  <button
                    onClick={() => {
                      setToolsSearchQuery('');
                      setToolsPage(1);
                    }}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--garage-text-3)'
                    }}
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              {toolsPersonaOptions.length > 0 && (
                <select
                  value={toolsPersonaFilter}
                  onChange={(e) => {
                    setToolsPersonaFilter(e.target.value);
                    setToolsPage(1); // Reset to first page on filter change
                  }}
                  title="Filter by persona"
                  style={{
                    flexShrink: 0,
                    padding: '8px 10px',
                    border: '1px solid var(--garage-border)',
                    borderRadius: '6px',
                    fontSize: '13px',
                    backgroundColor: 'var(--garage-bg)',
                    color: 'var(--garage-text)',
                    cursor: 'pointer',
                    maxWidth: '160px'
                  }}
                >
                  <option value="all">All personas</option>
                  {toolsPersonaOptions.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              )}
            </div>

            {topTools && topTools.length > 0 ? (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="garage-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px', textAlign: 'center', padding: '12px 8px' }}>
                          <input
                            type="checkbox"
                            style={{ cursor: 'pointer' }}
                            checked={selectedToolIds.length > 0 && selectedToolIds.length === Math.min(paginatedTools.length, 5)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Select up to 5 tools from current page
                                const toolsToSelect = paginatedTools.slice(0, 5).map(t => t.toolId);
                                setSelectedToolIds(toolsToSelect);
                              } else {
                                setSelectedToolIds([]);
                              }
                            }}
                            title="Select/Deselect visible tools (max 5)"
                          />
                        </th>
                        <th style={{ width: '50px', textAlign: 'center', padding: '12px 8px' }}>Rank</th>
                        <th style={{ minWidth: '200px', padding: '12px' }}>Tool</th>
                        <th style={{ width: '70px', textAlign: 'center', padding: '12px 8px' }}>Type</th>
                        <th style={{ width: '80px', textAlign: 'center', padding: '12px 8px' }}>Env</th>
                        <th style={{ width: '110px', textAlign: 'right', padding: '12px' }}>{getSessionsColumnLabel()}</th>
                        <th style={{ width: '100px', textAlign: 'center', padding: '12px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedTools.map((tool, index) => {
                        const globalIndex = (toolsPage - 1) * toolsPerPage + index;
                        const isSelected = selectedToolIds.includes(tool.toolId);
                        return (
                        <tr
                          key={tool.toolId}
                          onClick={() => handleToolRowClick(tool)}
                          style={{
                            cursor: 'pointer',
                            backgroundColor: isSelected ? 'var(--garage-blue-soft)' : 'transparent',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = 'var(--garage-bg-2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <td
                            style={{ textAlign: 'center', padding: '12px 8px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToolRowClick(tool)}
                              disabled={!isSelected && selectedToolIds.length >= 5}
                              style={{
                                cursor: (!isSelected && selectedToolIds.length >= 5) ? 'not-allowed' : 'pointer',
                                width: '16px',
                                height: '16px'
                              }}
                              title={(!isSelected && selectedToolIds.length >= 5) ? 'Maximum 5 tools can be selected' : 'Select for graph comparison'}
                            />
                          </td>
                          <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                            <RankDisplay rank={globalIndex} />
                          </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {tool.imageUrl && (
                              <img
                                src={tool.imageUrl}
                                alt={tool.toolTitle}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '6px',
                                  objectFit: 'cover',
                                  flexShrink: 0
                                }}
                              />
                            )}
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                color: 'var(--garage-text)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                lineHeight: '1.4'
                              }}>
                                {tool.toolTitle}
                              </div>
                              {tool.persona && (
                                <div style={{
                                  fontSize: '11px',
                                  color: 'var(--garage-text-3)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  marginTop: '2px',
                                  lineHeight: '1.3'
                                }}>
                                  {tool.persona}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                          <span className={`garage-tag garage-tag-${tool.toolType === 'App' ? 'blue' : 'orange'}`} style={{ fontSize: '10px', padding: '4px 8px' }}>
                            {tool.toolType}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                          <span className={`garage-tag garage-tag-${getEnvTagColor(tool.environment)}`} style={{ fontSize: '10px', padding: '4px 8px' }}>
                            {tool.environment || 'N/A'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: 'var(--garage-blue)', fontSize: '14px', padding: '12px' }}>
                          {formatNumber(getToolSessionsCount(tool))}
                        </td>
                        <td className="garage-dashboard-col-actions">
                          <div className="garage-dashboard-action-group">
                            <button
                              type="button"
                              className="garage-icon-button garage-icon-button-blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewToolSessions(tool);
                              }}
                              title="Analytics"
                            >
                              <BarChart2 size={14} />
                            </button>
                            <button
                              type="button"
                              className="garage-icon-button garage-icon-button-green"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewToolTopUsers(tool);
                              }}
                              title="Top Users"
                            >
                              <Users size={14} />
                            </button>
                          </div>
                        </td>
                        </tr>
                      );
                    })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination for Tools */}
                <Pagination
                  currentPage={toolsPage}
                  totalPages={totalToolsPages}
                  onPageChange={handleToolsPageChange}
                />
              </>
            ) : (toolsSearchQuery.trim() || toolsPersonaFilter !== 'all') ? (
              <div className="garage-placeholder-content">
                <p className="garage-placeholder-text">
                  {toolsSearchQuery.trim()
                    ? `No tools found matching "${toolsSearchQuery}"`
                    : `No tools found for persona "${toolsPersonaFilter}"`}
                </p>
                <button
                  onClick={() => {
                    setToolsSearchQuery('');
                    setToolsPersonaFilter('all');
                  }}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: 'var(--garage-blue)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="garage-placeholder-content">
                <p className="garage-placeholder-text">No tools data available</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: USERS DASHBOARD */}
        <div className="garage-dashboard-panel">

          {/* Top Users */}
          <div className="garage-dashboard-table-card">
          <div className="garage-dashboard-chart-header" style={{ padding: '16px 20px' }}>
            <div>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Users size={18} style={{ color: 'var(--garage-blue)' }} />
                <span>Top Users</span>
              </h3>
              <p style={{ fontSize: '12px', color: 'var(--garage-text-3)', marginLeft: '26px' }}>
                {usersSearchQuery.trim() ? (
                  <span>
                    {usersSearchLoading ? (
                      <>Searching users...</>
                    ) : (
                      <>
                        Found <strong style={{ color: 'var(--garage-blue)' }}>{displayUsers.length}</strong> user{displayUsers.length !== 1 ? 's' : ''} matching &quot;{usersSearchQuery.trim()}&quot; · Total active: <strong style={{ color: 'var(--garage-green)' }}>{stats.activeUsersInRange ?? stats.activeUsers7d ?? 0}</strong>
                      </>
                    )}
                  </span>
                ) : (
                  <span>
                    Top {displayUsers.length} users by sessions • Total active: <strong style={{ color: 'var(--garage-green)' }}>{stats.activeUsersInRange ?? stats.activeUsers7d ?? 0}</strong>
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div style={{
            padding: '12px 20px',
            backgroundColor: 'var(--garage-surface)',
            borderBottom: '1px solid var(--garage-border)'
          }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--garage-text-3)'
              }} />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={usersSearchQuery}
                onChange={(e) => {
                  setUsersSearchQuery(e.target.value);
                  setUsersPage(1); // Reset to first page on search
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 36px',
                  border: '1px solid var(--garage-border)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: 'var(--garage-bg)',
                  color: 'var(--garage-text)',
                  outline: 'none'
                }}
              />
              {usersSearchQuery && (
                <button
                  onClick={() => {
                    setUsersSearchQuery('');
                    setUsersPage(1);
                  }}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--garage-text-3)'
                  }}
                  title="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="garage-dashboard-chart-body" style={{ padding: '0' }}>
            {usersSearchLoading ? (
              <div className="garage-placeholder-content">
                <p className="garage-placeholder-text">
                  <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  Searching users...
                </p>
              </div>
            ) : displayUsers && displayUsers.length > 0 ? (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="garage-table garage-dashboard-users-table">
                    <thead>
                      <tr>
                        <th className="garage-dashboard-col-spacer" aria-hidden="true" />
                        <th className="garage-dashboard-col-rank">Rank</th>
                        <th className="garage-dashboard-col-user">User</th>
                        <th className="garage-dashboard-col-sessions">Sessions</th>
                        <th className="garage-dashboard-col-tools">Tools</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.map((user, index) => {
                        const globalIndex = (usersPage - 1) * usersPerPage + index;
                        return (
                          <tr
                            key={user.userEmail}
                            onClick={() => handleViewUserDetails(user)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="garage-dashboard-col-spacer" aria-hidden="true" />
                            <td className="garage-dashboard-col-rank">
                              <RankDisplay rank={globalIndex} />
                            </td>
                          <td className="garage-dashboard-col-user">
                            <div className="garage-dashboard-user-cell">
                              <div
                                className="garage-dashboard-user-avatar"
                                style={{ backgroundColor: getUserColor(index) }}
                              >
                                {getUserInitials(user.userEmail)}
                              </div>
                              <div className="garage-dashboard-user-info">
                                <div className="garage-dashboard-user-name" title={user.userName}>
                                  {user.userName}
                                </div>
                                <div className="garage-dashboard-user-email" title={user.userEmail}>
                                  {user.userEmail}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="garage-dashboard-col-sessions">
                            {user.sessions}
                          </td>
                          <td className="garage-dashboard-col-tools">
                            {user.toolsUsed || 0}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination for Users */}
                <Pagination
                  currentPage={usersPage}
                  totalPages={totalUsersPages}
                  onPageChange={handleUsersPageChange}
                />
              </>
            ) : usersSearchQuery.trim() ? (
              <div className="garage-placeholder-content">
                <p className="garage-placeholder-text">No users found matching "{usersSearchQuery}"</p>
                <button
                  onClick={() => setUsersSearchQuery('')}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: 'var(--garage-blue)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="garage-placeholder-content">
                <p className="garage-placeholder-text">No user data available</p>
              </div>
            )}
          </div>
        </div>
        {/* End of Top Users card */}

        </div>
        {/* End of RIGHT COLUMN */}

      </div>
      {/* End of two-column grid */}
      </GarageLoadingRegion>
        </>
      )}

      {/* Modals */}
      <ToolSessionDetailsModal
        isOpen={sessionModalOpen}
        tool={selectedTool}
        filters={filters}
        dateRangeLabel={getDateFilterDisplay()}
        onClose={() => {
          setSessionModalOpen(false);
          setSelectedTool(null);
        }}
      />

      <ToolTopUsersModal
        isOpen={toolUsersModalOpen}
        tool={selectedToolForUsers}
        topUsers={toolTopUsers}
        loading={loadingToolUsers}
        onClose={() => {
          setToolUsersModalOpen(false);
          setSelectedToolForUsers(null);
          setToolTopUsers([]);
        }}
        onUserClick={handleViewUserDetails}
        getUserInitials={getUserInitials}
        getUserColor={getUserColor}
      />

      <UserDetailsModal
        isOpen={userModalOpen}
        user={selectedUser}
        onClose={() => {
          setUserModalOpen(false);
          setSelectedUser(null);
        }}
      />

      <UserToolsModal
        isOpen={userToolsModalOpen}
        user={selectedUser}
        userTools={userTools}
        loading={loadingUserTools}
        onClose={() => {
          setUserToolsModalOpen(false);
          setUserTools([]);
        }}
        getUserInitials={getUserInitials}
        getUserColor={getUserColor}
      />

      {showTour && !showOnboarding && (
        <TourGuide
          steps={tourSteps}
          storageKey={tourStorageKey}
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}
    </div>
  );
};

export default GarageDashboardView;
